import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdmin } from '@/contexts/AdminContext';
import axios from 'axios';
import { 
  ArrowLeft, Plus, Trash2, Save, Eye, EyeOff, GripVertical, 
  ChevronUp, ChevronDown, Edit2, X, Play, Copy, RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SLIDE_TYPES = [
  { value: 'cover', label: '🎬 Copertina', desc: 'Prima slide con titolo grande' },
  { value: 'standard', label: '📄 Standard', desc: 'Testo con box evidenziato' },
  { value: 'feature', label: '✨ Feature', desc: '3 box con icone' },
  { value: 'benefits', label: '🎯 Benefici', desc: '4 card su sfondo colorato' },
  { value: 'steps', label: '📋 Passi', desc: 'Lista numerata' },
  { value: 'list', label: '📝 Lista', desc: 'Griglia 2 colonne' },
  { value: 'profiles', label: '👤 Profili', desc: 'Bottoni profilo' },
  { value: 'config', label: '⚙️ Config', desc: 'Lista con check' },
  { value: 'tips', label: '💡 Consigli', desc: 'Card con suggerimenti' },
  { value: 'qa', label: '❓ Q&A', desc: 'Domande frequenti' },
  { value: 'conclusion', label: '🏁 Finale', desc: 'Slide di chiusura' }
];

const ICONS = [
  'Target', 'Map', 'Bike', 'Download', 'Puzzle', 'Monitor', 'Settings', 
  'Navigation', 'Route', 'Layers', 'Compass', 'Cloud', 'Battery', 
  'Smartphone', 'Satellite', 'HelpCircle', 'Users', 'Calendar', 'Mountain',
  'MapPin', 'Radio', 'Zap', 'Eye', 'Play', 'Home', 'CheckCircle', 
  'ArrowRight', 'Globe', 'FileText', 'Search', 'Menu', 'Plus', 'Edit',
  'Upload', 'RefreshCw', 'Star', 'Save', 'TrendingUp', 'ZoomIn'
];

const AdminCorso = () => {
  const { isAdmin, loading: authLoading } = useAdmin();
  const navigate = useNavigate();
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);

  const adminPassword = localStorage.getItem('adminPassword') || '';

  const fetchSlides = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/corso/slides/all`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      setSlides(response.data || []);
    } catch (err) {
      console.error('Error fetching slides:', err);
      if (err.response?.status === 401) navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  }, [adminPassword, navigate]);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/admin/login');
      return;
    }
    fetchSlides();
  }, [isAdmin, authLoading, navigate, fetchSlides]);

  const handleSeedSlides = async () => {
    if (!window.confirm('Creare le 16 slide di default del corso OsmAnd?')) return;
    try {
      setSaving(true);
      const response = await axios.post(`${API}/corso/slides/seed`, {}, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      alert(response.data.success ? 'Slide create!' : response.data.message);
      fetchSlides();
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSlide = async (slideData) => {
    try {
      setSaving(true);
      if (slideData.id) {
        await axios.put(`${API}/corso/slides/${slideData.id}`, slideData, {
          headers: { 'X-Admin-Password': adminPassword }
        });
      } else {
        await axios.post(`${API}/corso/slides`, slideData, {
          headers: { 'X-Admin-Password': adminPassword }
        });
      }
      setEditingSlide(null);
      fetchSlides();
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSlide = async (slideId) => {
    if (!window.confirm('Eliminare questa slide?')) return;
    try {
      await axios.delete(`${API}/corso/slides/${slideId}`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      fetchSlides();
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleToggleActive = async (slide) => {
    try {
      await axios.put(`${API}/corso/slides/${slide.id}`, 
        { active: !slide.active },
        { headers: { 'X-Admin-Password': adminPassword } }
      );
      fetchSlides();
    } catch (err) {
      alert('Errore: ' + err.message);
    }
  };

  const handleMoveSlide = async (slideId, direction) => {
    const idx = slides.findIndex(s => s.id === slideId);
    if (idx === -1) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= slides.length) return;

    const newSlides = [...slides];
    [newSlides[idx], newSlides[newIdx]] = [newSlides[newIdx], newSlides[idx]];
    const orderData = newSlides.map((s, i) => ({ id: s.id, order: i }));
    
    try {
      await axios.post(`${API}/corso/slides/reorder`, orderData, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      fetchSlides();
    } catch (err) {
      alert('Errore: ' + err.message);
    }
  };

  const handleDuplicateSlide = async (slide) => {
    const newSlide = { ...slide, id: undefined, title: slide.title + ' (copia)', order: slides.length };
    delete newSlide.created_at;
    delete newSlide.updated_at;
    try {
      await axios.post(`${API}/corso/slides`, newSlide, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      fetchSlides();
    } catch (err) {
      alert('Errore: ' + err.message);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white py-6 shadow-lg">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard" className="text-white/80 hover:text-white">
                <ArrowLeft size={24} />
              </Link>
              <div>
                <h1 className="text-2xl font-bold">📚 Corso OsmAnd</h1>
                <p className="text-indigo-200">{slides.length} slide</p>
              </div>
            </div>
            <div className="flex gap-3">
              <a href="/corso-osmand" target="_blank" className="flex items-center bg-white/20 px-4 py-2 rounded-lg hover:bg-white/30">
                <Play size={18} className="mr-2" /> Anteprima
              </a>
              <button onClick={() => setEditingSlide({})} className="flex items-center bg-green-500 px-4 py-2 rounded-lg hover:bg-green-600">
                <Plus size={18} className="mr-2" /> Nuova Slide
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {slides.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <h3 className="text-xl font-semibold text-gray-700 mb-4">Nessuna slide</h3>
            <button onClick={handleSeedSlides} disabled={saving} className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50">
              <RefreshCw size={18} className={`inline mr-2 ${saving ? 'animate-spin' : ''}`} />
              Crea Slide di Default
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {slides.map((slide, index) => (
              <div key={slide.id} className={`bg-white rounded-lg shadow p-4 flex items-center gap-4 ${!slide.active ? 'opacity-50' : ''}`}>
                <GripVertical size={20} className="text-gray-400" />
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center font-bold text-indigo-600">
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
                      {SLIDE_TYPES.find(t => t.value === slide.slide_type)?.label || slide.slide_type}
                    </span>
                    {!slide.active && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Nascosta</span>}
                  </div>
                  <h3 className="font-semibold text-gray-900 truncate">{slide.title || '(Senza titolo)'}</h3>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => handleMoveSlide(slide.id, 'up')} disabled={index === 0} className="p-2 hover:bg-gray-100 rounded disabled:opacity-30" title="Su">
                    <ChevronUp size={18} />
                  </button>
                  <button onClick={() => handleMoveSlide(slide.id, 'down')} disabled={index === slides.length - 1} className="p-2 hover:bg-gray-100 rounded disabled:opacity-30" title="Giù">
                    <ChevronDown size={18} />
                  </button>
                  <button onClick={() => handleToggleActive(slide)} className={`p-2 rounded ${slide.active ? 'text-green-600' : 'text-gray-400'}`} title={slide.active ? 'Nascondi' : 'Mostra'}>
                    {slide.active ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                  <button onClick={() => handleDuplicateSlide(slide)} className="p-2 text-blue-600 hover:bg-blue-50 rounded" title="Duplica">
                    <Copy size={18} />
                  </button>
                  <button onClick={() => setEditingSlide(slide)} className="p-2 text-amber-600 hover:bg-amber-50 rounded" title="Modifica">
                    <Edit2 size={18} />
                  </button>
                  <button onClick={() => handleDeleteSlide(slide.id)} className="p-2 text-red-600 hover:bg-red-50 rounded" title="Elimina">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Editor Modal */}
      {editingSlide !== null && (
        <SlideEditor slide={editingSlide} onSave={handleSaveSlide} onClose={() => setEditingSlide(null)} saving={saving} />
      )}
    </div>
  );
};

// Editor Slide Semplificato
const SlideEditor = ({ slide, onSave, onClose, saving }) => {
  const [form, setForm] = useState({
    slide_type: 'standard',
    title: '',
    subtitle: '',
    badge: '',
    content: '',
    highlight: '',
    intro: '',
    note: '',
    footer: '',
    cta: '',
    bg_color_from: 'white',
    bg_color_to: 'white',
    icons: '',
    items_json: '[]',
    active: true,
    ...slide
  });

  const [passi, setPassi] = useState([]);

  useEffect(() => {
    try {
      const items = JSON.parse(form.items_json || '[]');
      setPassi(Array.isArray(items) ? items : []);
    } catch {
      setPassi([]);
    }
  }, []);

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const updatePasso = (index, field, value) => {
    const newPassi = [...passi];
    newPassi[index] = { ...newPassi[index], [field]: value };
    setPassi(newPassi);
    setForm(prev => ({ ...prev, items_json: JSON.stringify(newPassi) }));
  };

  const addPasso = () => {
    const newPassi = [...passi, { num: passi.length + 1, text: '', icon: 'CheckCircle' }];
    setPassi(newPassi);
    setForm(prev => ({ ...prev, items_json: JSON.stringify(newPassi) }));
  };

  const removePasso = (index) => {
    const newPassi = passi.filter((_, i) => i !== index);
    setPassi(newPassi);
    setForm(prev => ({ ...prev, items_json: JSON.stringify(newPassi) }));
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-start justify-center z-50 overflow-y-auto py-8">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-t-2xl">
          <h2 className="text-xl font-bold">{slide.id ? '✏️ Modifica Slide' : '➕ Nuova Slide'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg"><X size={24} /></button>
        </div>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Tipo Slide */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Tipo di Slide</label>
            <div className="grid grid-cols-3 gap-2">
              {SLIDE_TYPES.map(t => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => updateField('slide_type', t.value)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${form.slide_type === t.value ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}`}
                >
                  <div className="font-medium text-sm">{t.label}</div>
                  <div className="text-xs text-gray-500">{t.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Titolo */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">📌 Titolo</label>
            <input type="text" value={form.title} onChange={(e) => updateField('title', e.target.value)}
              className="w-full border-2 rounded-lg px-4 py-3 text-lg font-semibold focus:border-indigo-500 focus:outline-none"
              placeholder="Titolo della slide" />
          </div>

          {/* Sottotitolo (solo per cover) */}
          {form.slide_type === 'cover' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Sottotitolo</label>
                <input type="text" value={form.subtitle} onChange={(e) => updateField('subtitle', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2" placeholder="Sottotitolo" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Badge</label>
                <input type="text" value={form.badge} onChange={(e) => updateField('badge', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2" placeholder="Es: Vademecum ufficiale" />
              </div>
            </div>
          )}

          {/* Contenuto */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">📝 Contenuto Principale</label>
            <textarea value={form.content} onChange={(e) => updateField('content', e.target.value)}
              rows={5} className="w-full border-2 rounded-lg px-4 py-3 focus:border-indigo-500 focus:outline-none"
              placeholder="Scrivi il testo principale della slide..." />
            <p className="text-xs text-gray-500 mt-1">Usa "Invio" per andare a capo</p>
          </div>

          {/* Highlight/Note */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">💡 Box Evidenziato (opzionale)</label>
            <textarea value={form.highlight || form.note} onChange={(e) => { updateField('highlight', e.target.value); updateField('note', e.target.value); }}
              rows={2} className="w-full border rounded-lg px-3 py-2"
              placeholder="Testo importante da evidenziare..." />
          </div>

          {/* Passi / Elementi */}
          <div className="bg-indigo-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-semibold text-gray-700">📋 Passi / Elementi</label>
              <button type="button" onClick={addPasso} className="text-sm bg-indigo-600 text-white px-3 py-1.5 rounded-lg hover:bg-indigo-700">
                + Aggiungi Passo
              </button>
            </div>
            
            {passi.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">Nessun passo. Clicca "Aggiungi Passo" per iniziare.</p>
            ) : (
              <div className="space-y-2">
                {passi.map((passo, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-white p-3 rounded-lg">
                    <span className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                      {idx + 1}
                    </span>
                    <select
                      value={passo.icon || ''}
                      onChange={(e) => updatePasso(idx, 'icon', e.target.value)}
                      className="border rounded px-2 py-1.5 text-sm w-32"
                    >
                      <option value="">Icona...</option>
                      {ICONS.map(i => <option key={i} value={i}>{i}</option>)}
                    </select>
                    <input
                      type="text"
                      value={passo.text || passo.tip || passo.desc || ''}
                      onChange={(e) => {
                        updatePasso(idx, 'text', e.target.value);
                        updatePasso(idx, 'tip', e.target.value);
                        updatePasso(idx, 'desc', e.target.value);
                      }}
                      className="flex-1 border rounded px-3 py-1.5"
                      placeholder="Descrizione del passo..."
                    />
                    <button onClick={() => removePasso(idx)} className="p-1.5 text-red-600 hover:bg-red-50 rounded">
                      <X size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer / CTA */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Footer</label>
              <input type="text" value={form.footer} onChange={(e) => updateField('footer', e.target.value)}
                className="w-full border rounded-lg px-3 py-2" placeholder="Testo in fondo" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Call to Action</label>
              <input type="text" value={form.cta} onChange={(e) => updateField('cta', e.target.value)}
                className="w-full border rounded-lg px-3 py-2" placeholder="Invito all'azione" />
            </div>
          </div>

          {/* Attiva/Nascondi */}
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => updateField('active', e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-indigo-600" />
            <span className="font-medium">Slide visibile nel corso</span>
          </label>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-5 border-t bg-gray-50 rounded-b-2xl">
          <button onClick={onClose} className="px-6 py-2.5 border rounded-lg hover:bg-gray-100">Annulla</button>
          <button onClick={() => onSave(form)} disabled={saving}
            className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center font-semibold">
            <Save size={18} className="mr-2" />
            {saving ? 'Salvataggio...' : 'Salva Slide'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminCorso;
